import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function ContactForm({ compact = false }) {
  const { currentLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    description: ''
  });
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const content = {
    en: {
      title: "Let's Discuss Your Project",
      subtitle: 'Tell us about your idea and we\'ll get back to you within 24 hours.',
      name: 'Your Name',
      namePlaceholder: 'John Smith',
      email: 'Email Address',
      emailPlaceholder: 'john@company.com',
      phone: 'Phone Number',
      phonePlaceholder: '+1 555 123 4567',
      projectType: 'Project Type',
      projectTypePlaceholder: 'Select project type',
      description: 'Project Description',
      descriptionPlaceholder: 'Brief overview of your project, goals, and timeline...',
      submit: 'Discuss Project',
      submitting: 'Sending...',
      successTitle: 'Message Received',
      successMessage: 'Thank you for reaching out. We\'ll review your project and respond within 24 hours.',
      errorMessage: 'Something went wrong. Please try again or email us directly.',
      projectTypes: [
        { value: 'saas-mvp', label: 'SaaS / MVP Development' },
        { value: 'ai-system', label: 'AI & Prediction Systems' },
        { value: 'financial', label: 'Financial Software' },
        { value: 'platform', label: 'Business Platform / CRM' },
        { value: 'analytics', label: 'Analytics & Dashboard' },
        { value: 'other', label: 'Other' }
      ]
    },
    tr: {
      title: 'Projenizi Konuşalım',
      subtitle: 'Fikrinizi bize anlatın, 24 saat içinde size dönüş yapalım.',
      name: 'Adınız',
      namePlaceholder: 'Ahmet Yılmaz',
      email: 'E-posta Adresi',
      emailPlaceholder: 'ahmet@sirket.com',
      phone: 'Telefon Numarası',
      phonePlaceholder: '+90 532 123 4567',
      projectType: 'Proje Tipi',
      projectTypePlaceholder: 'Proje tipini seçin',
      description: 'Proje Açıklaması',
      descriptionPlaceholder: 'Projenizin kısa özeti, hedefleri ve zaman planı...',
      submit: 'Projeyi Tartışalım',
      submitting: 'Gönderiliyor...',
      successTitle: 'Mesajınız Alındı',
      successMessage: 'Bizimle iletişime geçtiğiniz için teşekkürler. Projenizi inceleyip 24 saat içinde döneceğiz.',
      errorMessage: 'Bir hata oluştu. Lütfen tekrar deneyin veya doğrudan e-posta gönderin.',
      projectTypes: [
        { value: 'saas-mvp', label: 'SaaS / MVP Geliştirme' },
        { value: 'ai-system', label: 'AI & Tahminleme Sistemleri' },
        { value: 'financial', label: 'Finansal Yazılım' },
        { value: 'platform', label: 'İş Platformu / CRM' },
        { value: 'analytics', label: 'Analitik & Dashboard' },
        { value: 'other', label: 'Diğer' }
      ]
    },
    de: {
      title: 'Lassen Sie uns Ihr Projekt besprechen',
      subtitle: 'Erzählen Sie uns von Ihrer Idee und wir melden uns innerhalb von 24 Stunden.',
      name: 'Ihr Name',
      namePlaceholder: 'Max Mustermann',
      email: 'E-Mail-Adresse',
      emailPlaceholder: 'max@unternehmen.de',
      phone: 'Telefonnummer',
      phonePlaceholder: '+49 170 123 4567',
      projectType: 'Projekttyp',
      projectTypePlaceholder: 'Projekttyp auswählen',
      description: 'Projektbeschreibung',
      descriptionPlaceholder: 'Kurze Übersicht über Ihr Projekt, Ziele und Zeitplan...',
      submit: 'Projekt besprechen',
      submitting: 'Wird gesendet...',
      successTitle: 'Nachricht erhalten',
      successMessage: 'Vielen Dank für Ihre Kontaktaufnahme. Wir prüfen Ihr Projekt und antworten innerhalb von 24 Stunden.',
      errorMessage: 'Etwas ist schief gelaufen. Bitte versuchen Sie es erneut oder senden Sie uns direkt eine E-Mail.',
      projectTypes: [
        { value: 'saas-mvp', label: 'SaaS / MVP Entwicklung' },
        { value: 'ai-system', label: 'KI & Prognosesysteme' },
        { value: 'financial', label: 'Finanzsoftware' },
        { value: 'platform', label: 'Business-Plattform / CRM' },
        { value: 'analytics', label: 'Analytics & Dashboard' },
        { value: 'other', label: 'Sonstiges' }
      ]
    }
  };

  const t = content[currentLanguage] || content.en;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          language: currentLanguage
        }),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', projectType: '', description: '' });
      } else {
        const data = await response.json();
        setErrorMessage(data.detail || t.errorMessage);
        setStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setErrorMessage(t.errorMessage);
      setStatus('error');
    }
  };

  // Success State
  if (status === 'success') {
    return (
      <div className={`max-w-xl mx-auto text-center ${compact ? 'py-8' : 'py-12'}`}>
        <div 
          className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-5"
          style={{ 
            background: 'rgba(46, 125, 50, 0.15)',
            border: '1px solid rgba(46, 125, 50, 0.25)'
          }}
        >
          <CheckCircle className="w-6 h-6" style={{ color: '#2E7D32' }} />
        </div>
        <h3 className="text-xl font-medium mb-2 tracking-tight" style={{ color: 'var(--text-on-light-primary)' }}>{t.successTitle}</h3>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-on-light-muted)' }}>{t.successMessage}</p>
      </div>
    );
  }

  const inputClass = `w-full px-4 py-3 rounded-md text-sm transition-all duration-300 focus:outline-none`;

  return (
    <form onSubmit={handleSubmit} className={`max-w-xl mx-auto ${compact ? 'space-y-5' : 'space-y-6'}`} data-testid="contact-form">
      {/* Header - Hide in compact mode */}
      {!compact && (
        <div className="text-center mb-10">
          <h3 className="text-2xl font-medium mb-3 tracking-tight" style={{ color: 'var(--text-on-light-primary)' }}>{t.title}</h3>
          <p className="text-sm" style={{ color: 'var(--text-on-light-muted)' }}>{t.subtitle}</p>
        </div>
      )}

      {/* Name & Email Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: 'var(--text-on-light-muted)' }}>
            {t.name}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t.namePlaceholder}
            required
            data-testid="contact-name"
            className={inputClass}
            style={{
              background: '#FFFFFF',
              border: '1px solid rgba(15, 118, 110, 0.15)',
              color: 'var(--text-on-light-primary)'
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgba(15, 118, 110, 0.4)'}
            onBlur={(e) => e.target.style.borderColor = 'rgba(15, 118, 110, 0.15)'}
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: 'var(--text-on-light-muted)' }}>
            {t.email}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t.emailPlaceholder}
            required
            data-testid="contact-email"
            className={inputClass}
            style={{
              background: '#FFFFFF',
              border: '1px solid rgba(15, 118, 110, 0.15)',
              color: 'var(--text-on-light-primary)'
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgba(15, 118, 110, 0.4)'}
            onBlur={(e) => e.target.style.borderColor = 'rgba(15, 118, 110, 0.15)'}
          />
        </div>
      </div>

      {/* Phone Number */}
      <div>
        <label htmlFor="phone" className="block text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: 'var(--text-on-light-muted)' }}>
          {t.phone}
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder={t.phonePlaceholder}
          data-testid="contact-phone"
          className={inputClass}
          style={{
            background: '#FFFFFF',
            border: '1px solid rgba(15, 118, 110, 0.15)',
            color: 'var(--text-on-light-primary)'
          }}
          onFocus={(e) => e.target.style.borderColor = 'rgba(15, 118, 110, 0.4)'}
          onBlur={(e) => e.target.style.borderColor = 'rgba(15, 118, 110, 0.15)'}
        />
      </div>

      {/* Project Type */}
      <div>
        <label htmlFor="projectType" className="block text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: 'var(--text-on-light-muted)' }}>
          {t.projectType}
        </label>
        <select
          id="projectType"
          name="projectType"
          value={formData.projectType}
          onChange={handleChange}
          required
          data-testid="contact-project-type"
          className={`${inputClass} appearance-none cursor-pointer`}
          style={{ 
            background: '#FFFFFF',
            border: '1px solid rgba(15, 118, 110, 0.15)',
            color: 'var(--text-on-light-primary)',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748B'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, 
            backgroundRepeat: 'no-repeat', 
            backgroundPosition: 'right 12px center', 
            backgroundSize: '14px' 
          }}
        >
          <option value="" disabled style={{ background: '#FFFFFF' }}>{t.projectTypePlaceholder}</option>
          {t.projectTypes.map((type) => (
            <option key={type.value} value={type.value} style={{ background: '#FFFFFF' }}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: 'var(--text-on-light-muted)' }}>
          {t.description}
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder={t.descriptionPlaceholder}
          rows={compact ? 3 : 4}
          required
          data-testid="contact-description"
          className={`${inputClass} resize-none`}
          style={{
            background: '#FFFFFF',
            border: '1px solid rgba(15, 118, 110, 0.15)',
            color: 'var(--text-on-light-primary)'
          }}
          onFocus={(e) => e.target.style.borderColor = 'rgba(15, 118, 110, 0.4)'}
          onBlur={(e) => e.target.style.borderColor = 'rgba(15, 118, 110, 0.15)'}
        />
      </div>

      {/* Error Message */}
      {status === 'error' && (
        <div 
          className="flex items-center gap-3 p-3 rounded-md"
          style={{
            background: 'rgba(220, 38, 38, 0.1)',
            border: '1px solid rgba(220, 38, 38, 0.2)'
          }}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#DC2626' }} />
          <p className="text-xs" style={{ color: '#DC2626' }}>{errorMessage}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={status === 'loading'}
        data-testid="contact-submit"
        className="w-full flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-md font-medium text-sm transition-all duration-500 ease-premium disabled:cursor-not-allowed disabled:opacity-50"
        style={{ 
          background: status !== 'loading' ? 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)' : 'rgba(15, 118, 110, 0.5)',
          color: '#FFFFFF',
          boxShadow: status !== 'loading' ? '0 0 20px -6px rgba(15, 118, 110, 0.35)' : 'none' 
        }}
      >
        {status === 'loading' ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t.submitting}
          </>
        ) : (
          <>
            {t.submit}
            <Send className="w-4 h-4" />
          </>
        )}
      </button>
    </form>
  );
}
